/**
 * OfflineService — localStorage-based offline queue for workout logs.
 * Simplified version (no SQLite dependency for web builds).
 * On native with SQLite installed, could be upgraded later.
 */

const QUEUE_KEY = 'duofit_offline_queue';
const CACHE_KEY = 'duofit_today_cache';
const GPS_QUEUE_KEY = 'duofit_gps_queue';
const FINISH_QUEUE_KEY = 'duofit_finish_queue';

export class OfflineService {
  static isOnline() {
    return navigator.onLine;
  }

  static async queueWorkoutLog(data) {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    queue.push({ ...data, queued_at: new Date().toISOString(), synced: false });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }

  static getPendingCount() {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    return queue.filter(q => !q.synced).length;
  }

  static async sync(authToken) {
    const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    const pending = queue.filter(q => !q.synced);
    if (pending.length === 0) return 0;

    let synced = 0;
    for (const item of pending) {
      try {
        const res = await fetch('/api/workout-logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify(item),
        });
        if (res.ok) {
          item.synced = true;
          synced++;
        }
      } catch { /* will retry next sync */ }
    }

    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    // Clean up synced items older than 24h
    const cleaned = queue.filter(q => !q.synced || (Date.now() - new Date(q.queued_at).getTime() < 86400000));
    localStorage.setItem(QUEUE_KEY, JSON.stringify(cleaned));

    return synced;
  }

  static async queueGpsBatch(sessionId, points) {
    const queue = JSON.parse(localStorage.getItem(GPS_QUEUE_KEY) || '[]');
    queue.push({ session_id: sessionId, points, queued_at: new Date().toISOString(), synced: false });
    localStorage.setItem(GPS_QUEUE_KEY, JSON.stringify(queue));
  }

  static async syncGpsBatches(authToken) {
    const queue = JSON.parse(localStorage.getItem(GPS_QUEUE_KEY) || '[]');
    const pending = queue.filter(q => !q.synced);
    if (pending.length === 0) return 0;

    let synced = 0;
    for (const item of pending) {
      try {
        const res = await fetch(`/api/running-sessions/${item.session_id}/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ points: item.points }),
        });
        if (res.ok) {
          item.synced = true;
          synced++;
        }
      } catch { /* will retry next sync */ }
    }

    const cleaned = queue.filter(q => !q.synced || (Date.now() - new Date(q.queued_at).getTime() < 86400000));
    localStorage.setItem(GPS_QUEUE_KEY, JSON.stringify(cleaned));

    return synced;
  }

  static async queueSessionFinish(sessionId, finishData) {
    const queue = JSON.parse(localStorage.getItem(FINISH_QUEUE_KEY) || '[]');
    queue.push({ session_id: sessionId, data: finishData, queued_at: new Date().toISOString(), synced: false });
    localStorage.setItem(FINISH_QUEUE_KEY, JSON.stringify(queue));
  }

  static async syncSessionFinish(authToken) {
    const queue = JSON.parse(localStorage.getItem(FINISH_QUEUE_KEY) || '[]');
    const pending = queue.filter(q => !q.synced);
    if (pending.length === 0) return 0;

    let synced = 0;
    for (const item of pending) {
      try {
        const res = await fetch(`/api/running-sessions/${item.session_id}/finish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify(item.data),
        });
        if (res.ok) {
          item.synced = true;
          synced++;
        }
      } catch { /* will retry next sync */ }
    }

    const cleaned = queue.filter(q => !q.synced || (Date.now() - new Date(q.queued_at).getTime() < 86400000));
    localStorage.setItem(FINISH_QUEUE_KEY, JSON.stringify(cleaned));

    return synced;
  }

  static cacheTodayPlan(plan) {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ plan, cached_at: Date.now() }));
  }

  static getCachedTodayPlan() {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { plan, cached_at } = JSON.parse(raw);
    // TTL 12h
    if (Date.now() - cached_at > 12 * 60 * 60 * 1000) return null;
    return plan;
  }
}
